"use client"

import { ProductCard } from "@/components/shared/product-card"
import { ProductsSearchDataResponse } from "@repo/api-shared/src/endpoints"
import { useEffect, useState } from "react"
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination"
import { cn } from "@/lib/utils"
import { Loader } from "lucide-react"

const MAX_ROWS = 4

// Helper to calculate items per page based on screen width
function getItemsPerPage(width?: number) {
    // if (width >= 1024) return 3 * MAX_ROWS // lg: 3 columns * 4 rows
    // if (width >= 768) return 2 * MAX_ROWS // md: 2 columns * 4 rows
    // return 1 * MAX_ROWS // default: 1 column * 4 rows
    return 3 * MAX_ROWS
}

export function ProductsList({
    products,
    selectedPriceRange,
    loading,
}: {
    products: Array<ProductsSearchDataResponse["products"][string]>
    selectedPriceRange: [number, number]
    loading: boolean
}) {
    const itemsPerPage = getItemsPerPage()
    const totalPages = Math.ceil(products.length / itemsPerPage)

    // Initialize page state
    const [currentPage, setCurrentPage] = useState(0)
    const [previousTotalPages, setPreviousTotalPages] = useState(totalPages)

    // Reset page only when total pages changes
    useEffect(() => {
        if (totalPages !== previousTotalPages) {
            setCurrentPage(0)
            setPreviousTotalPages(totalPages)
        }
    }, [totalPages, previousTotalPages])

    if (products.length === 0) {
        return (
            <div className="border border-gray-300 p-8 py-10 text-center">
                <h2 className="mb-2 text-xl font-bold">Товары не найдены</h2>
                <p>Попробуйте изменить параметры фильтрации</p>
            </div>
        )
    }

    // Calculate current page products
    const startIndex = currentPage * itemsPerPage
    const currentProducts = products.slice(
        startIndex,
        startIndex + itemsPerPage,
    )

    return (
        <div className="relative mb-72 flex flex-col gap-6">
            {loading && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/40">
                    <Loader
                        className="h-8 w-8 animate-spin text-primary"
                        style={{ marginTop: "30vh" }}
                    />
                </div>
            )}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {currentProducts.map((product) => (
                    <ProductCard
                        key={product.handle}
                        selectedPriceRange={selectedPriceRange}
                        {...product}
                    />
                ))}
            </div>

            {totalPages > 1 && (
                <Pagination>
                    <PaginationContent>
                        <PaginationItem>
                            <PaginationPrevious
                                onMouseDown={() =>
                                    setCurrentPage((p) => Math.max(0, p - 1))
                                }
                            />
                        </PaginationItem>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <PaginationItem key={i}>
                                <PaginationLink
                                    isActive={currentPage === i}
                                    className={cn(
                                        i === currentPage &&
                                            "bg-primary text-white hover:bg-primary/80 hover:text-white",
                                    )}
                                    onMouseDown={() => setCurrentPage(i)}
                                >
                                    {i + 1}
                                </PaginationLink>
                            </PaginationItem>
                        ))}
                        <PaginationItem>
                            <PaginationNext
                                onMouseDown={() => {
                                    setCurrentPage((p) =>
                                        Math.min(totalPages - 1, p + 1),
                                    )
                                }}
                            />
                        </PaginationItem>
                    </PaginationContent>
                </Pagination>
            )}
        </div>
    )
}
